import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Plus, Search, Upload, Check, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import API_URL from '@/lib/apiConfig';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/contexts/AuthContext';
import ProjectDetailPage from '@/components/projects/ProjectDetailPage';
import CreateProjectWizard from '@/components/projects/CreateProjectWizard';
import ImportProjectModal from '@/components/projects/widgets/ImportProjectModal';
import PaginationControls from '@/components/common/PaginationControls';

// Default page size for the Projects list. Independent of the Time
// Management Quick Add picker, which calls /api/projects/projects with
// limit=500 directly — that caller is unaffected by this constant.
const DEFAULT_PAGE_SIZE = 25;

const SERVICE_LINES = [
  'ucaas', 'networking', 'cloud', 'security', 'ai_product', 'other',
];

// Real methodology values (the synthetic "all" is now represented by an
// empty selection — no project_type sent → backend returns every type).
const METHODOLOGY_OPTIONS = [
  { id: 'waterfall', label: 'Waterfall' },
  { id: 'agile', label: 'Agile' },
  { id: 'waves_threads', label: 'Waves & Threads' },
];

// Multi-select methodology filter. `selected` is an array of ids; an empty
// array means "all". Each row shows a Check when active; clicking toggles it.
function MethodologyFilter({ selected, onToggle, onClear }) {
  const count = selected.length;
  const triggerLabel =
    count === 0
      ? 'All methodologies'
      : count === 1
        ? METHODOLOGY_OPTIONS.find((o) => o.id === selected[0])?.label
        : `${count} methodologies`;

  const Row = ({ active, onClick, children, testid }) => (
    <button
      type="button"
      role="menuitemcheckbox"
      aria-checked={active}
      onClick={onClick}
      data-testid={testid}
      className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs text-left text-boss-text hover:bg-boss-elevated transition-colors"
    >
      <span className="w-4 inline-flex justify-center">
        {active ? <Check size={14} className="text-violet-400" /> : null}
      </span>
      <span>{children}</span>
    </button>
  );

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          data-testid="filter-methodology"
          className="boss-input h-8 px-2 text-xs inline-flex items-center justify-between gap-2 min-w-[150px]"
        >
          <span className={count === 0 ? 'text-boss-text-muted' : 'text-boss-text'}>
            {triggerLabel}
          </span>
          <ChevronDown size={12} className="text-boss-text-muted shrink-0" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          className="z-[60] min-w-[180px] rounded-md border border-boss-border bg-boss-surface shadow-xl p-1 animate-in fade-in-0 zoom-in-95 duration-100"
          data-testid="filter-methodology-menu"
        >
          <Row active={count === 0} onClick={onClear} testid="filter-methodology-all">
            All methodologies
          </Row>
          <div className="my-1 h-px bg-boss-border" />
          {METHODOLOGY_OPTIONS.map((o) => (
            <Row
              key={o.id}
              active={selected.includes(o.id)}
              onClick={() => onToggle(o.id)}
              testid={`filter-methodology-${o.id}`}
            >
              {o.label}
            </Row>
          ))}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

const HEALTH_DOT = {
  green: '#4ade80',
  yellow: '#facc15',
  red: '#f87171',
};

const STATUS_PILL = {
  planning: 'bg-boss-elevated text-boss-text-muted',
  active: 'bg-blue-500/15 text-blue-400',
  on_hold: 'bg-yellow-500/15 text-yellow-400',
  closed: 'bg-green-500/15 text-green-400',
};

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const m = typeof iso === 'string' && /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

function debounce(fn, ms) {
  let t = null;
  return (...args) => {
    if (t) clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}

function TypeChip({ type }) {
  if (!type) return null;
  const label = type === 'waves_threads' ? 'W&T' : String(type).toUpperCase();
  return (
    <span
      data-testid="project-type-chip"
      className="inline-flex items-center h-5 px-2 rounded bg-violet-500/15 text-violet-400 text-[10px] font-semibold tracking-wide"
    >
      {label}
    </span>
  );
}

function StatusPill({ status }) {
  if (!status) return null;
  const cls = STATUS_PILL[status] ?? 'bg-boss-elevated text-boss-text-muted';
  const label = String(status).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <span className={`inline-flex items-center h-5 px-2 rounded text-[10px] font-medium ${cls}`}>
      {label}
    </span>
  );
}

export default function ProjectsTab({ urlProjectId, onProjectIdChange }) {
  const perms = usePermissions();
  const { user } = useAuth();
  const myUserId = user?.user_id ?? user?._id;
  const canCreate = perms.hasPermission('projects.project.create');

  // Multi-select methodologies. [] = all. Toggling adds/removes an id.
  const [methodologyTypes, setMethodologyTypes] = useState([]);
  const toggleMethodology = (id) =>
    setMethodologyTypes((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  const [filters, setFilters] = useState({ status: '', service_line: '', pm_id: '', q: '', mine: false });
  const [qDraft, setQDraft] = useState('');
  // Server-side pagination. The Time Management Quick Add picker
  // calls /api/projects/projects?limit=500 directly and is unaffected
  // by these page controls.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [projects, setProjects] = useState([]);
  const [total, setTotal] = useState(0);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [wizardOpen, setWizardOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  // detailProjectId is now URL-driven when running inside ProjectsPortal,
  // but we keep a local fallback so the component still works standalone.
  const [localDetailId, setLocalDetailId] = useState(null);
  const detailProjectId = onProjectIdChange ? (urlProjectId ?? null) : localDetailId;
  const setDetailProjectId = (id) => {
    if (onProjectIdChange) onProjectIdChange(id);
    else setLocalDetailId(id);
  };

  // Debounced search. Setting q resets page=1 below via the filter-
  // change effect.
  const applyQ = useMemo(() => debounce((v) => {
    setFilters((f) => ({ ...f, q: v }));
  }, 250), []);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    // Append one project_type param per selected methodology; the backend
    // matches any of them ($in). Empty selection = no param = all types.
    methodologyTypes.forEach((t) => params.append('project_type', t));
    if (filters.status) params.set('status', filters.status);
    if (filters.service_line) params.set('service_line', filters.service_line);
    if (filters.pm_id) params.set('pm_id', filters.pm_id);
    if (filters.q) params.set('q', filters.q);
    if (filters.mine && myUserId) params.set('assigned_user_id', myUserId);
    params.set('limit', String(pageSize));
    params.set('skip', String(Math.max(0, (page - 1) * pageSize)));
    try {
      const res = await api.get(`${API_URL}/api/projects/projects?${params.toString()}`);
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setProjects(Array.isArray(data) ? data : data.items ?? []);
      setTotal(Array.isArray(data) ? data.length : data.total ?? 0);
    } catch {
      // keep stale data
    } finally {
      setLoading(false);
    }
  }, [methodologyTypes, filters, page, pageSize, myUserId]);

  // Reset page=1 whenever a filter that changes the result set
  // changes. Page-size changes also reset to keep the displayed range
  // honest (e.g., on page 4 of 25/page, switching to 100/page would
  // otherwise show "Showing 76–100 of 12").
  useEffect(() => {
    setPage(1);
  }, [methodologyTypes, filters, pageSize]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.get(`${API_URL}/api/projects/assignable-users?role=pm`);
      if (!res.ok) {
        toast.error('Could not load PM list.');
        return;
      }
      const data = await res.json();
      setUsers(Array.isArray(data) ? data : data.items ?? []);
    } catch {
      toast.error('Could not load PM list.');
    }
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);
  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const pmById = useMemo(() => {
    const m = new Map();
    users.forEach((u) => m.set(u.user_id ?? u._id, u));
    return m;
  }, [users]);

  const hasAnyFilter = filters.status || filters.service_line || filters.pm_id || filters.q || filters.mine || methodologyTypes.length > 0;
  const clearFilters = () => { setFilters({ status: '', service_line: '', pm_id: '', q: '', mine: false }); setQDraft(''); setMethodologyTypes([]); };

  if (detailProjectId) {
    return (
      <ProjectDetailPage
        projectId={detailProjectId}
        onBack={() => setDetailProjectId(null)}
      />
    );
  }

  return (
    <section data-testid="projects-list-page">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-boss-text" data-testid="projects-title">Projects</h2>
        {canCreate && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setImportOpen(true)}
              data-testid="import-project-button"
              className="btn-secondary inline-flex items-center gap-2 text-sm"
            >
              <Upload size={15} />
              Import Project
            </button>
            <button
              type="button"
              onClick={() => setWizardOpen(true)}
              data-testid="new-project-button"
              className="btn-primary inline-flex items-center gap-2 text-sm"
            >
              <Plus size={15} />
              New Project
            </button>
          </div>
        )}
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="projects-filter-row">
        <MethodologyFilter
          selected={methodologyTypes}
          onToggle={toggleMethodology}
          onClear={() => setMethodologyTypes([])}
        />

        <select
          data-testid="filter-status"
          value={filters.status}
          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          className="boss-input h-8 px-2 text-xs"
        >
          <option value="">All statuses</option>
          <option value="planning">Planning</option>
          <option value="active">Active</option>
          <option value="on_hold">On hold</option>
          <option value="closed">Closed</option>
        </select>

        <select
          data-testid="filter-service-line"
          value={filters.service_line}
          onChange={(e) => setFilters((f) => ({ ...f, service_line: e.target.value }))}
          className="boss-input h-8 px-2 text-xs"
        >
          <option value="">All service lines</option>
          {SERVICE_LINES.map((s) => (
            <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
          ))}
        </select>

        <select
          data-testid="filter-pm"
          value={filters.pm_id}
          onChange={(e) => setFilters((f) => ({ ...f, pm_id: e.target.value }))}
          className="boss-input h-8 px-2 text-xs"
        >
          <option value="">All PMs</option>
          {users
            .slice()
            .sort((a, b) => (a.name ?? a.display_name ?? '').localeCompare(b.name ?? b.display_name ?? ''))
            .map((u) => (
              <option key={u.user_id ?? u._id} value={u.user_id ?? u._id}>
                {u.name ?? u.display_name}
              </option>
            ))}
        </select>

        <div className="relative" data-testid="filter-search">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-boss-text-muted pointer-events-none" />
          <input
            type="text"
            value={qDraft}
            onChange={(e) => { setQDraft(e.target.value); applyQ(e.target.value); }}
            placeholder="Search projects…"
            className="boss-input pl-7 h-8 w-56 text-xs"
          />
        </div>

        {myUserId ? (
          <button
            type="button"
            onClick={() => setFilters((f) => ({ ...f, mine: !f.mine }))}
            className={`h-8 px-3 rounded-md text-xs font-medium border transition-colors ${
              filters.mine
                ? 'border-violet-500 bg-violet-500/15 text-violet-300'
                : 'border-boss-border text-boss-text-secondary hover:bg-boss-elevated'
            }`}
            data-testid="filter-assigned-to-me"
          >
            Assigned to me
          </button>
        ) : null}

        {hasAnyFilter && (
          <button
            type="button"
            onClick={clearFilters}
            data-testid="clear-filters"
            className="ml-auto text-xs text-boss-text-secondary hover:text-boss-text underline-offset-4 hover:underline transition-colors"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="min-h-[200px] flex items-center justify-center">
          <div className="animate-spin w-7 h-7 rounded-full border-2 border-violet-500 border-t-transparent" />
        </div>
      ) : projects.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <p className="text-sm font-medium text-boss-text mb-1">No projects yet.</p>
          <p className="text-xs text-boss-text-muted mb-4">Create your first project to get started.</p>
          {canCreate && (
            <button
              type="button"
              onClick={() => setWizardOpen(true)}
              className="btn-primary inline-flex items-center gap-2 text-sm"
              data-testid="empty-new-project"
            >
              <Plus size={14} />
              New Project
            </button>
          )}
        </div>
      ) : (
        <div className="glass-card overflow-hidden" data-testid="projects-table-wrap">
          <table className="w-full text-sm" data-testid="projects-table">
            <thead>
              <tr className="h-11 bg-boss-elevated text-[11px] text-boss-text-muted uppercase tracking-wide border-b border-boss-border">
                <th className="text-left px-4 font-medium">Name</th>
                <th className="text-left px-3 font-medium">Type</th>
                <th className="text-left px-3 font-medium hidden sm:table-cell">Service line</th>
                <th className="text-left px-3 font-medium">Status</th>
                <th className="text-left px-3 font-medium">Health</th>
                <th className="text-left px-3 font-medium hidden md:table-cell">PM</th>
                <th className="text-left px-3 font-medium hidden lg:table-cell">Dates</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => {
                const pm = pmById.get(p.pm_id);
                return (
                  <tr
                    key={p.project_id ?? p._id}
                    onClick={() => setDetailProjectId(p.project_id ?? p._id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setDetailProjectId(p.project_id ?? p._id);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label={`Open project ${p.name}`}
                    className="h-12 border-t border-boss-border hover:bg-boss-elevated cursor-pointer transition-colors focus:bg-boss-elevated focus:outline-none focus:ring-1 focus:ring-violet-500"
                    data-testid={`project-row-${p.project_id ?? p._id}`}
                  >
                    <td className="px-4 max-w-[260px] truncate font-semibold text-boss-text" title={p.description ?? p.name}>
                      {p.name}
                    </td>
                    <td className="px-3">
                      <TypeChip type={p.project_type} />
                    </td>
                    <td className="px-3 text-boss-text-secondary capitalize hidden sm:table-cell">
                      {(p.service_line ?? '').replace(/_/g, ' ')}
                    </td>
                    <td className="px-3">
                      <StatusPill status={p.status} />
                    </td>
                    <td className="px-3">
                      <span className="inline-flex items-center gap-1.5 text-xs">
                        <span
                          className="inline-block w-2 h-2 rounded-full"
                          style={{ backgroundColor: HEALTH_DOT[p.health] ?? '#94a3b8' }}
                        />
                        <span className="capitalize text-boss-text-secondary">{p.health}</span>
                      </span>
                    </td>
                    <td className="px-3 hidden md:table-cell">
                      <span className="text-xs text-boss-text-secondary truncate max-w-[120px]">
                        {pm?.name ?? pm?.display_name ?? '—'}
                      </span>
                    </td>
                    <td className="px-3 text-xs text-boss-text-muted whitespace-nowrap hidden lg:table-cell">
                      {fmtDate(p.start_date)} → {fmtDate(p.target_end_date)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="px-3 border-t border-boss-border">
            <PaginationControls
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              loading={loading}
              testid="projects-pagination"
            />
          </div>
        </div>
      )}

      {wizardOpen && (
        <CreateProjectWizard
          open={wizardOpen}
          onClose={() => setWizardOpen(false)}
          onCreated={(projectId) => {
            setWizardOpen(false);
            setDetailProjectId(projectId);
          }}
        />
      )}

      <ImportProjectModal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={(project) => {
          // Same handoff as the wizard: refresh the list, then jump straight
          // into the newly created project.
          fetchProjects();
          const pid = project?.project_id;
          if (pid) setDetailProjectId(pid);
        }}
      />
    </section>
  );
}
